import { CellProps } from 'react-table'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import {
  PlainHeader,
  sortableColumn,
  StackedCell,
  StatusBadge,
  TableColumn,
} from '../../../../../../components'
import { FaturaAtendimentoList } from '../../core/_models'
import { FaturaAtendimentosActionsCell } from './FaturaAtendimentosActionsCell'
import { ProtocoloAtendimentoCell } from './ProtocoloAtendimentoCell'
import { TFunction } from '_metronic/i18n/Metronici18n'

type Opcoes = {
  /** Admin vê só protocolo e o valor (rotulado como "Valor a pagar"). */
  isAdmin: boolean
  /** Itens só podem ser alterados/removidos com a fatura ainda "Gerada". */
  podeEditar: boolean
  /** Visão do responsável da instituição (rota com `:idEmpresa`): detalhe do atendimento reduzido. */
  isResponsavel: boolean
}

export const faturaAtendimentosColumns = (
  t: TFunction,
  { isAdmin, podeEditar, isResponsavel }: Opcoes
): ReadonlyArray<TableColumn<FaturaAtendimentoList>> => {
  // Protocolo (abre o detalhe) com a especialidade como apoio; os dois seguem ordenáveis.
  const protocolo = sortableColumn<FaturaAtendimentoList>({
    id: 'atendimento.protocolo',
    useQueryRequest,
    fields: [
      { field: 'atendimento.protocolo', label: t('cols.protocolo') },
      { field: 'atendimento.especialidade.nome', label: t('cols.especialidade') },
    ],
    Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
      <ProtocoloAtendimentoCell
        atendimento={row.original.atendimento}
        isResponsavel={isResponsavel}
      />
    ),
  })

  const valorEmpresa = (label: string) =>
    sortableColumn<FaturaAtendimentoList>({
      id: 'valorEmpresa',
      cellWidth: '152px',
      align: 'end',
      useQueryRequest,
      fields: [{ field: 'valorEmpresa', label }],
      Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
        <StackedCell tabular primary={row.original.valorEmpresaFormatado} />
      ),
    })

  if (isAdmin) return [protocolo, valorEmpresa(t('cols.valor_pagar'))]

  return [
    protocolo,
    sortableColumn<FaturaAtendimentoList>({
      id: 'profissional.pessoa.nome',
      hideOnMobile: true,
      useQueryRequest,
      fields: [{ field: 'profissional.pessoa.nome', label: t('cols.profissional') }],
      Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
        <StackedCell primary={row.original.profissional?.nome} />
      ),
    }),
    valorEmpresa(t('cols.valor_faturar')),
    sortableColumn<FaturaAtendimentoList>({
      id: 'valorProfissional',
      cellWidth: '152px',
      align: 'end',
      hideOnMobile: true,
      useQueryRequest,
      fields: [{ field: 'valorProfissional', label: t('cols.valor_pagar') }],
      Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
        <StackedCell tabular primary={row.original.valorProfissionalFormatado} />
      ),
    }),
    sortableColumn<FaturaAtendimentoList>({
      id: 'ativa',
      cellWidth: '112px',
      useQueryRequest,
      fields: [{ field: 'ativa', label: t('cols.status') }],
      Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
        <StatusBadge active={row.original.ativa} />
      ),
    }),
    // Pendência em destaque (badge vermelho com o texto "Sim") só quando existe; sem pendência,
    // texto simples — o que pede atenção salta na lista.
    {
      Header: () => <PlainHeader>{t('cols.pendente')}</PlainHeader>,
      id: 'pendencia',
      cellWidth: '104px',
      Cell: ({ row }: CellProps<FaturaAtendimentoList>) =>
        row.original.pendencia ? (
          <StatusBadge active={false} labels={['Não', 'Sim']} />
        ) : (
          <StackedCell primary='Não' />
        ),
    },
    ...(podeEditar
      ? [
          {
            Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
            id: 'actions',
            cellWidth: '96px',
            align: 'end',
            Cell: ({ row }: CellProps<FaturaAtendimentoList>) => (
              <FaturaAtendimentosActionsCell faturaAtendimento={row.original} />
            ),
          } as TableColumn<FaturaAtendimentoList>,
        ]
      : []),
  ]
}
